import React, { useState } from 'react';
import { buildOptimizedImageUrl, buildSrcSet, isImageProxyEnabled } from '../../utils/imageProxy';

interface OptimizedImageProps extends Omit<React.ImgHTMLAttributes<HTMLImageElement>, 'src' | 'srcSet'> {
  src: string;
  /**
   * `sizes` HTML attribute. Critical for srcset — without it browsers default to 100vw
   * and fetch the largest variant. Pass the actual rendered width as a hint:
   *  - grid thumbnails: "(max-width: 768px) 33vw, 200px"
   *  - modal hero: "95vh"
   *  - full-bleed: "100vw"
   */
  sizes?: string;
  /** Render with eager loading + fetchpriority=high (use for above-the-fold images). */
  priority?: boolean;
  /** Width to request from the proxy when no srcset is desired (single-resolution mode). */
  width?: number;
  /** JPEG/WebP quality, 1-100. Default 85. */
  quality?: number;
}

/**
 * Drop-in replacement for <img> that routes through the image proxy.
 * Falls back to the original CDN URL if the proxy is not configured or the upstream errors.
 */
export const OptimizedImage: React.FC<OptimizedImageProps> = ({
  src,
  sizes = '100vw',
  priority,
  width,
  quality = 85,
  loading,
  decoding,
  fetchPriority,
  crossOrigin,
  onError,
  ...rest
}) => {
  const [errored, setErrored] = useState(false);

  const useProxy = isImageProxyEnabled() && !errored;
  const proxiedSrc = useProxy ? buildOptimizedImageUrl(src, width ?? 1920, quality) : src;
  const srcSet = useProxy && !width ? buildSrcSet(src, quality) : undefined;

  return (
    <img
      {...rest}
      src={proxiedSrc}
      srcSet={srcSet}
      sizes={srcSet ? sizes : undefined}
      loading={priority ? 'eager' : loading ?? 'lazy'}
      decoding={decoding ?? 'async'}
      fetchPriority={fetchPriority ?? (priority ? 'high' : 'auto')}
      crossOrigin={crossOrigin ?? 'anonymous'}
      onError={(e) => {
        // If the proxy fails, swap to the original CDN URL silently — give
        // the original a chance before bubbling onError to the parent.
        if (useProxy) {
          setErrored(true);
          return;
        }
        onError?.(e);
      }}
    />
  );
};

export default OptimizedImage;
